<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <filter-manager
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :display-advanced="false"
      :filters="filtersConfig"
      @search="onSearchHandler"
    />
    <custom-separator class="mt-1 mb-4" text="Resultado de Búsqueda" />
    <custom-result-not-found
      v-if="results.length == 0"
      :text="'No hay información disponible.'"
    />
    <template v-else>
      <result-row
        v-for="(row, index) of results"
        :key="`result-${index}`"
        class="mb-3"
        :data="row"
        @on-edit="onEditHandler($event)"
        @on-print="onPrintHandler($event)"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="goToPageHandler"
      />
    </template>
  </div>
</template>

<script>
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import {
  getInitFilters as _getInitFilters,
  search as _search,
  downloadTemplate as _downloadTemplate,
} from '@/services/provider/contract-document-configuration.api.js';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import ResultRow from './components/ResultRow.vue';
import storeModuleNames from '@/utils/constants/storeModuleNames.js';

const DEFAULT_PAGE_SIZE = 5;
const CONTRACT_DOCUMENT_CONFIGURATION_MANAGEMENT_ROUTE_NAME =
  'contractDocumentConfigurationManagement';

export default {
  name: 'ContractDocumentConfigurationManagementMain',
  mixins: [alertMixin, storeMixin],
  moduleName:
    storeModuleNames.ProviderContractDocumentConfigurationManagementModuleName,
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    ResultRow,
  },
  data: () => ({
    filterModel: {
      academicYear: null,
      serviceType: null,
      status: null,
    },
    filterOptions: {
      academicYears: [],
      ServiceTypes: [],
      Statuses: [],
    },
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: [],
  }),
  computed: {
    headerConfig() {
      return {
        title: 'Configuración de Documento de Contrato',
        icon: 'fas fa-user-tie',
        routes: [
          'Inicio',
          'Proveedor',
          'Configuración de Documento de Contrato',
        ],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Añadir',
          icon: 'fas fa-file-plus pr-1',
          variant: 'success',
          visible: this.canUploadDocuments,
          click: () => this.onAddHandler(),
        },
      ];
    },
    canUploadDocuments() {
      return this.userHasPermissions(
        'rempro.providers.addcontractdocumentconfiguration'
      );
    },
    hasPersistedInfo() {
      return (
        !!this.moduleData?.ContractDocumentConfigurationManagementMain &&
        (this.previousRoute?.name ===
          'contractDocumentConfigurationManagement' ||
          this.previousRoute?.name === 'contractTemplateHistory')
      );
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          clearable: false,
          validations: 'required',
          error: 'El año Académico es requerido.',
        },
        {
          name: 'serviceType',
          component: 'CustomDropDown',
          options: this.filterOptions.serviceTypes,
          placeholder: 'Tipo de Contrato',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-4',
        },
      ];
    },
  },
  async mounted() {
    if (!this.hasPersistedInfo) {
      await this.getInitFilters();
      this.filterModel = {
        ...this.filterModel,
        serviceType: null,
        status: null,
      };
      this.results = [];
      return;
    } else {
      this.searchFromDetail(this.pagination.currentPage);
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => (vm.previousRoute = from));
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async getInitFilters() {
      const { filters, collections } = (await _getInitFilters()).data;
      this.filterModel = {
        ...this.filterModel,
        ...filters,
      };
      this.filterOptions = {
        ...this.filterOptions,
        ...collections,
      };
    },
    fillPayload(page) {
      const payload = {
        academicYearId: this.filterModel.academicYear?.id,
        statusId: this.filterModel.status?.id,
        contractTypeId: this.filterModel.serviceType?.id,
        pageIndex: page ? page : 1,
        pageSize: this.pagination.pageSize,
      };
      return payload;
    },
    async onSearchHandler() {
      const pageIndex = 1;

      const payload = this.fillPayload(pageIndex);
      this.search(payload);
    },
    async searchFromDetail(pageIndex) {
      const payload = this.fillPayload(pageIndex);
      this.search(payload);
    },
    async search(payload) {
      const res = (await _search(payload)).data;
      this.results = res.data;
      this.pagination = {
        ...this.pagination,
        count: res.count,
        currentPage: res.currentPage,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    async goToPageHandler(page) {
      const payload = this.fillPayload(page);
      await this.search(payload);
    },
    onEditHandler(contractTemplateId) {
      this.$router.push({
        name: CONTRACT_DOCUMENT_CONFIGURATION_MANAGEMENT_ROUTE_NAME,
        params: {
          contractTemplateId,
        },
      });
    },
    onPrintHandler(contractTemplateId) {
      return _downloadTemplate(contractTemplateId);
    },
    onAddHandler() {
      this.$router.push({
        name: CONTRACT_DOCUMENT_CONFIGURATION_MANAGEMENT_ROUTE_NAME,
      });
    },
  },
};
</script>

<style></style>
